import type DevToolsProtocol from 'devtools-protocol';
import { DbRecordedEventByCategory, ISODateTimeString, OptionallyObservableMap } from '../../State/EvidenceDB/DatabaseTypes';
import { InstrumentationEventScriptElementAdded } from '../Events/InstrumentationEvent';




export type HowCausationInferred =
	'url-and-request-id-matched-in-same-frame' |
	'request-id-matched' |
	'url-loaded-in-same-frame' |
	'url-loaded-in-parents-frame' |
	'url-loaded-in-same-tab' |
	'matches-initiator' |
	'new-initiator' |
	'matches-call-frame' |
	'new-call-frame' |	
	'defaulted-to-page-frame' |
	'defaulted-to-parents-page-frame' |
	'frame-navigated-from' |
	'frame-attached' |
	'script-appended' |
	never ;

export type CausationEdge<W extends HowCausationInferred = HowCausationInferred, T extends CausationNode = CausationNode> = {
	node: T;
	howInferred: W;
}

export type CausationEdgeByIndex<W extends HowCausationInferred = HowCausationInferred> = {
	causeIndex: number;
	howInferred: W;
}

/**
 * In process. May later use this to annotate graph edges with how the causation was inferred.
 * For now, this is a no-op that help documentation.
 */
export const causationNodeWithHowFound = <W extends HowCausationInferred, T extends CausationNode>(
	howInferred: W,
	cause: T | undefined | false
): CausationEdge<W, T> | undefined =>
	cause ? { howInferred, node: cause } : undefined;



export type DevToolsFrameId = DevToolsProtocol.Page.FrameId;
export type UUID = ReturnType<typeof crypto.randomUUID>;
interface CausationNodeBase {
	causationNodeIndex: number;
	causeType: string;
	timeStampIso: ISODateTimeString | undefined;
	causedBy: CausationEdge[];
	willCause: CausationEdge[];
}

export interface HasEventIndex {
	eventIndex: number;
}
// interface CausedBy {
// 	code?: CausationNodeInitiator | CausationNodeCallFrame; 
// 	url?: CausationNode;
// }
interface CausationNodeNetworkRequestBase extends CausationNodeBase, HasEventIndex {
	requestId: string;
}
type RequestWillBeSentEvent = DevToolsProtocol.Network.RequestWillBeSentEvent;
type RequestWillBsSentEventRequest = NonNullable<RequestWillBeSentEvent['request']>;
type RequestWillBsSentEventRedirectResponse = NonNullable<RequestWillBeSentEvent['redirectResponse']>;
type RequestWillBsSentEventRedirectResponseHeaders = RequestWillBsSentEventRedirectResponse['headers'];

export interface CausationNodeRequestWillBeSentBase extends
	CausationNodeNetworkRequestBase, Pick<RequestWillBeSentEvent, 'initiator' | 'requestId' | 'frameId'>, Pick<RequestWillBsSentEventRequest, 'url'> {
}
/**
 * This causation node is created for the `Network.RequestWillBeSentEvent` events
 * (other than those for redirects), which maps the request to the URL it was sent to
 * and includes information about code that triggered the network request.
 *
 * When we work backwards in time, we know that the navigation to the requested URL
 * was caused by any code from within the call stack that exists within the event's
 * `initiator` field.
 *
 */

export interface CausationNodeRequestWillBeSent extends CausationNodeRequestWillBeSentBase {
	causeType: 'Network.requestWillBeSent';
}
/**
 * This causation node is created for the `Network.RequestWillBeSentEvent`
 * that include redirect responses, indicating that the request for one URL
 * will be replaced with a redirected URL.
 *
 * When we work backwards in time, we know that the navigation to
 * the url in field `redirectedToUrl` was caused by a redirect from
 * field `url`, for which there will be an earlier request for that url,
 * and the code that caused it.
 */
export interface CausationNodeRedirect extends CausationNodeRequestWillBeSentBase {
	causeType: 'Network.requestWillBeSent:redirect';
	redirectedToUrl: RequestWillBsSentEventRedirectResponseHeaders['location'];
}
/**
 * This causation node is created for the `Page.FrameAttachedEvent`,
 * which maps the frame to the frame that loaded it and the code that
 * triggered the creation of the frame (e.g. creating an iframe).
 *
 * When we work backwards in time, we know that events that take place
 * within this frame are caused by the frame being created, and that
 * the frame was created by the code in the parent frame.
 *
 */

export interface CausationNodeNetworkRequestDebuggerEvent extends CausationNodeNetworkRequestBase {
	causeType: `${ Exclude< DbRecordedEventByCategory<"Debugger.Network">["eventName"], 'Network.requestWillBeSent' > }`;
	eventName: DbRecordedEventByCategory<"Debugger.Network">["eventName"]
}

export interface CausationNodePageFrameAttached extends CausationNodeBase, HasEventIndex, DevToolsProtocol.Page.FrameAttachedEvent {
	causeType: 'Page.frameAttached';
}
/**
 * This causation node is created for the `Page.FrameStartedNavigatingEvent`,
 * which maps the frame to the URL loaded into it.
 *
 * When we work backwards in time, we know that events that take place
 * within the frame after this event (and before any subsequent
 * `FrameStartedNavigatingEvent`s) are caused by this URL.
 */

// export interface CausationNodePageFrameStartedNavigating extends CausationNodeBase, HasEventIndex, DevToolsProtocol.Page.FrameStartedNavigatingEvent {
// 	causeType: 'page-frame-started-navigating';
// }
// /**
//  * This causation node is created for the `Page.FrameRequestedNavigationEvent`,
//  * which maps the frame to the URL loaded into it.
//  *
//  * When we work backwards in time, we know that events that take place
//  * within the frame after this event (and before any subsequent
//  * `FrameStartedNavigatingEvent`s) are caused by this URL.
//  */

export interface CausationNodePageFrameRequestedNavigation extends CausationNodeBase, HasEventIndex, DevToolsProtocol.Page.FrameRequestedNavigationEvent {
	causeType: 'Page.frameRequestedNavigation';
}

export interface CausationNodePageFrameNavigatedWithinDocument extends CausationNodeBase, HasEventIndex, DevToolsProtocol.Page.NavigatedWithinDocumentEvent {
	causeType: 'Page.navigatedWithinDocument';
}

export interface CausationNodeCallFrame extends CausationNodeBase, HasEventIndex, DevToolsProtocol.Runtime.CallFrame {
	causeType: 'call-frame';
	windowFrameId?: DevToolsFrameId;
}

export interface CausationNodeInitiator extends CausationNodeBase, HasEventIndex, Omit<DevToolsProtocol.Network.Initiator, 'stack'> {
	causeType: 'initiator';
	windowFrameId?: DevToolsFrameId;
}

export interface CausationNodeScriptElementAppended extends CausationNodeBase, HasEventIndex, InstrumentationEventScriptElementAdded {
	causeType: 'script-appended';
}


export type CausationNodeNetwork =
	CausationNodeRequestWillBeSent |
	CausationNodeRedirect |
	CausationNodeNetworkRequestDebuggerEvent |
	never;

export type CausationNodePageFrame =
	CausationNodePageFrameAttached |
//	CausationNodePageFrameStartedNavigating |
	CausationNodePageFrameNavigatedWithinDocument |
	CausationNodePageFrameRequestedNavigation |
	never;

export type CausationNodeCausedByBrowser =
	CausationNodePageFrame |
	CausationNodeNetwork;

export type CausationNodeCausedByScriptCode =
	CausationNodeCallFrame |
	CausationNodeInitiator |
	// CausationNodeCode |
	never;

export type CausationNode =
	CausationNodeCausedByBrowser |
	CausationNodeCausedByScriptCode |
	CausationNodeScriptElementAppended |
	never;

export type CausationNodeType = CausationNode['causeType'];

export type IndexedCausationNode = Omit<CausationNode, 'causedBy' | 'willCause'> & {causedBy: CausationEdgeByIndex[]; willCause: CausationEdgeByIndex[];};

export const causationNodeToIndexedCausationNode = (causationNode: CausationNode): IndexedCausationNode => {
	return {
		...causationNode,
		causedBy: causationNode.causedBy.map(({howInferred, node}) =>
			({howInferred, causeIndex: node.causationNodeIndex})),
		willCause: causationNode.willCause.map(({howInferred, node}) =>
			({howInferred, causeIndex: node.causationNodeIndex})),
	};
};

export const causationNodesToIndexedCausationNodes = (causationNodes: CausationNode[]): IndexedCausationNode[] => {
	return causationNodes.map( causationNodeToIndexedCausationNode );
}

export const indexedCausationNodeToCausationNode = (causationNodesByIndex: OptionallyObservableMap<number, CausationNode> = new Map()) => (indexedCausationNode: IndexedCausationNode): CausationNode => {
	const {causedBy, willCause, ...node} = indexedCausationNode;
	const {causationNodeIndex} = node;
	const causationNode = {
		...node,
		willCause: [] as CausationEdge[],
		causedBy: [] as CausationEdge[],
	} as CausationNode;
	causationNodesByIndex.set(causationNodeIndex, causationNode);
	for (const {howInferred, causeIndex} of willCause) {
		const willCauseNode = causationNodesByIndex.get(causeIndex);
		if (willCauseNode == null) continue;
		causationNode.willCause.push({howInferred, node: willCauseNode});
		(willCauseNode.causedBy as CausationEdge[]).push({howInferred, node: causationNode});
	}
	for (const {howInferred, causeIndex} of causedBy) {
		const causedByNode = causationNodesByIndex.get(causeIndex);
		if (causedByNode == null) continue;
		(causationNode.causedBy as CausationEdge[]).push({howInferred, node: causedByNode});
		causedByNode.willCause.push({howInferred, node: causationNode});
	}
	return causationNode;
}

export const indexedCausationNodesToCausationNodes = (indexedCausationNodes: IndexedCausationNode[]): CausationNode[] => {
	const toNode = indexedCausationNodeToCausationNode();
	return indexedCausationNodes.map(toNode);
}
